import React, { useState, useEffect } from 'react';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faEnvelope, faKey, faSave, faEdit, faCircleCheck, faPause, faStop, faCircleXmark, faCircleExclamation, faUniversity, faMoneyBillTransfer, faCreditCard, faShieldAlt, faTimes, faUserCheck, faUserPlus, faEye, faEyeSlash, faCheck, faSpinner, faBullhorn, faCalendarAlt, faIdCard, faPhone, faLock, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../Api';
import ConfirmationModal from '../../components/ConfirmationModal';
import SuccessModal from '../../components/SuccessModal';
import ProfilePhotoUploader from '../../components/ProfilePhotoUploader';

const UserProfile = () => {
    const { user, refreshUser } = useAuth();
    const location = useLocation();
    const [loading, setLoading] = useState(false);

    // Supervisors and F2F interviewers go through the mandatory profile step (phone +
    // verified email). CATI interviewers never do.
    const hasProfileGate = user?.role === 'superviseur' || (user?.role === 'enqueteur' && !user?.is_cati);
    const emailVerifyBase = user?.role === 'superviseur' ? '/supervisors' : '/interviewers';
    // They must enter their phone before they can use the rest of the app.
    const profileIncomplete = hasProfileGate && user?.profile_complete === false;
    // Hard block: a truly-new account that has never verified any email.
    const emailVerificationRequired = hasProfileGate && user?.email_verification_required === true;
    // Soft prompt: not truly verified (never confirmed OR sentinel-backfilled), but not
    // hard-blocked. email_verified === false covers both; the hard-block case is a subset.
    const emailNotVerified = hasProfileGate && user?.email_verified === false;
    // Fully-gated (blocked from the rest of the app) when phone missing OR email unverified (new).
    const accessBlocked = profileIncomplete || emailVerificationRequired;
    const [showBlockedModal, setShowBlockedModal] = useState(false);
    const showBlockedModalDismiss = useModalDismiss(() => setShowBlockedModal(false), { backdropClose: false, enabled: showBlockedModal });
    
    // Edit modals
    const [showEditBasicModal, setShowEditBasicModal] = useState(false);
    const showEditBasicModalDismiss = useModalDismiss(() => setShowEditBasicModal(false), { backdropClose: false, enabled: showEditBasicModal });
    const [showEditPaymentModal, setShowEditPaymentModal] = useState(false);
    const showEditPaymentModalDismiss = useModalDismiss(() => setShowEditPaymentModal(false), { backdropClose: false, enabled: showEditPaymentModal });
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    const showChangePasswordModalDismiss = useModalDismiss(() => setShowChangePasswordModal(false), { backdropClose: false, enabled: showChangePasswordModal });
    const [showEditConfidentialModal, setShowEditConfidentialModal] = useState(false);
    const showEditConfidentialModalDismiss = useModalDismiss(() => setShowEditConfidentialModal(false), { backdropClose: false, enabled: showEditConfidentialModal });

    // Confidential data state
    const [confidentialData, setConfidentialData] = useState(null);
    const [loadingConfidential, setLoadingConfidential] = useState(false);
    const [confidentialFormData, setConfidentialFormData] = useState({
        cin: '',
        phone: '',
        bank_account_number: ''
    });
    
    // Form data
    const [basicFormData, setBasicFormData] = useState({
        name: '',
        email: '',
        cin: '',
        phone: ''
    });
    
    const [paymentFormData, setPaymentFormData] = useState({
        payment_method_type: '',
        bank_name: '',
        transfer_service: '',
        custom_bank_name: '',
        custom_transfer_service: '',
        bank_account_number: '',
        payment_name: '',
        cin: ''
    });
    
    const [passwordFormData, setPasswordFormData] = useState({
        current_password: '',
        new_password: '',
        new_password_confirmation: ''
    });
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Email verification flow (supervisors + F2F interviewers). Email can only be set/changed by
    // confirming a 6-digit code, so any new email is always verified.
    const [showEmailVerifyModal, setShowEmailVerifyModal] = useState(false);
    const showEmailVerifyModalDismiss = useModalDismiss(() => setShowEmailVerifyModal(false), { backdropClose: false, enabled: showEmailVerifyModal });
    const [emailVerifyStep, setEmailVerifyStep] = useState('request'); // 'request' | 'code'
    const [emailVerifyForm, setEmailVerifyForm] = useState({ email: '', code: '' });
    const [emailVerifyLoading, setEmailVerifyLoading] = useState(false);
    const [emailVerifyError, setEmailVerifyError] = useState('');
    const [emailVerifyInfo, setEmailVerifyInfo] = useState('');

    // Inline email verification inside the "Modifier les Informations" form.
    const [basicVerifyStage, setBasicVerifyStage] = useState('none'); // 'none' | 'prompt' | 'sent'
    const [basicVerifyCode, setBasicVerifyCode] = useState('');
    const [basicVerifyLoading, setBasicVerifyLoading] = useState(false);
    const [basicVerifyMsg, setBasicVerifyMsg] = useState('');

    const [error, setError] = useState('');
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    // Delegation state (for administrateur_general only)
    const [delegationUsers, setDelegationUsers] = useState([]);

    // Global delegation state
    const [globalDelegationSettings, setGlobalDelegationSettings] = useState(null);
    const [showGlobalDelegationModal, setShowGlobalDelegationModal] = useState(false);
    const showGlobalDelegationModalDismiss = useModalDismiss(() => setShowGlobalDelegationModal(false), { backdropClose: false, enabled: showGlobalDelegationModal });
    const [globalDelegate1Id, setGlobalDelegate1Id] = useState('');
    const [globalDelegate2Id, setGlobalDelegate2Id] = useState('');
    const [processingGlobalDelegation, setProcessingGlobalDelegation] = useState(false);
    const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);

    // Completed formations state (for interviewers, controllers, supervisors)
    const [completedFormations, setCompletedFormations] = useState([]);
    const [loadingFormations, setLoadingFormations] = useState(false);

    // Update form data when user data is available
    React.useEffect(() => {
        if (user) {
            setBasicFormData({
                name: user.name || '',
                email: user.email || ''
            });
            setPaymentFormData({
                payment_method_type: user.payment_method_type || '',
                bank_name: user.bank_name || '',
                transfer_service: user.transfer_service || '',
                custom_bank_name: '',
                custom_transfer_service: '',
                payment_name: user.payment_name || user.name || ''
            });
        }
    }, [user]);

    // Fetch delegation data for administrateur_general
    useEffect(() => {
        console.log('User role:', user?.role);
        if (user?.role === 'administrateur_general') {
            console.log('Fetching delegation data for administrateur_general');
            fetchDelegationUsers();
            fetchGlobalDelegationSettings();
        }
    }, [user]);

    // Fetch completed formations for interviewers, controllers, supervisors
    useEffect(() => {
        if (user && (user.role === 'enqueteur' || user.role === 'controleur' || user.role === 'superviseur')) {
            fetchCompletedFormations();
        }
    }, [user]);

    // Gate UX for supervisors with an incomplete profile: a "must complete" modal
    // whenever they try to navigate elsewhere (ProtectedRoute bounces them back here
    // with location.state.profileBlocked).
    useEffect(() => {
        if (!accessBlocked) {
            setShowBlockedModal(false);
            return;
        }
        if (location.state?.profileBlocked) {
            setShowBlockedModal(true);
        }
    }, [accessBlocked, location.key]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch confidential data for interviewers, controllers, supervisors
    useEffect(() => {
        if (user && (user.role === 'enqueteur' || user.role === 'controleur' || user.role === 'superviseur')) {
            fetchConfidentialData();
        }
    }, [user]);

    const fetchCompletedFormations = async () => {
        try {
            setLoadingFormations(true);
            const response = await api.get('/formations/completed');
            if (response.data.success) {
                setCompletedFormations(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching completed formations:', error);
        } finally {
            setLoadingFormations(false);
        }
    };

    const fetchConfidentialData = async () => {
        try {
            setLoadingConfidential(true);
            const response = await api.get('/confidential-data/me/masked');
            if (response.data.success) {
                setConfidentialData(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching confidential data:', error);
        } finally {
            setLoadingConfidential(false);
        }
    };

    const handleEditBasicClick = async () => {
        setError('');
        // Fetch confidential data for CIN and phone
        let cin = '';
        let phone = '';
        if (user?.role === 'enqueteur' || user?.role === 'controleur' || user?.role === 'superviseur') {
            try {
                const response = await api.get('/confidential-data/me');
                if (response.data.success && response.data.data) {
                    cin = response.data.data.cin || '';
                    phone = response.data.data.phone || '';
                }
            } catch (error) {
                console.error('Error fetching confidential data:', error);
            }
        }
        setBasicFormData({
            name: user.name || '',
            email: user.email || '',
            cin: cin,
            phone: phone
        });
        // Reset the inline email-verification section each time the modal opens.
        setBasicVerifyStage('none');
        setBasicVerifyCode('');
        setBasicVerifyMsg('');
        setBasicVerifyLoading(false);
        setShowEditBasicModal(true);
    };

    const handleEditPaymentClick = async () => {
        setError('');
        // Fetch CIN and RIB from confidential data
        let cin = '';
        let rib = '';
        try {
            const response = await api.get('/confidential-data/me');
            if (response.data.success && response.data.data) {
                cin = response.data.data.cin || '';
                rib = response.data.data.bank_account_number || '';
            }
        } catch (error) {
            console.error('Error fetching confidential data:', error);
        }
        setPaymentFormData({
            payment_method_type: user.payment_method_type || '',
            bank_name: user.bank_name || '',
            transfer_service: user.transfer_service || '',
            custom_bank_name: '',
            custom_transfer_service: '',
            bank_account_number: rib,
            payment_name: user.payment_name || user.name || '',
            cin: cin
        });
        setShowEditPaymentModal(true);
    };

    const handleEditConfidentialClick = async () => {
        setError('');
        // Fetch unmasked data for editing
        try {
            const response = await api.get('/confidential-data/me');
            if (response.data.success && response.data.data) {
                setConfidentialFormData({
                    cin: response.data.data.cin || '',
                    phone: response.data.data.phone || '',
                    bank_account_number: response.data.data.bank_account_number || ''
                });
            } else {
                setConfidentialFormData({
                    cin: '',
                    phone: '',
                    bank_account_number: ''
                });
            }
        } catch (error) {
            console.error('Error fetching confidential data for editing:', error);
            setConfidentialFormData({
                cin: '',
                phone: '',
                bank_account_number: ''
            });
        }
        setShowEditConfidentialModal(true);
    };

    const handleSaveConfidentialData = async () => {
        setLoading(true);
        setError('');

        // Supervisors must provide a phone to unlock the app
        if (profileIncomplete && !confidentialFormData.phone) {
            setError('Vous devez renseigner votre numéro de téléphone pour accéder à l\'application.');
            setLoading(false);
            return;
        }

        // Validate phone prefix if phone is provided
        if (confidentialFormData.phone && confidentialFormData.phone.length >= 2) {
            const prefix = confidentialFormData.phone.substring(0, 2);
            if (!['05', '06', '07'].includes(prefix)) {
                setError('Le numéro de téléphone doit commencer par 05, 06 ou 07');
                setLoading(false);
                return;
            }
        }

        // Validate phone length if provided
        if (confidentialFormData.phone && confidentialFormData.phone.length !== 10 && confidentialFormData.phone.length > 0) {
            setError('Le numéro de téléphone doit contenir exactement 10 chiffres');
            setLoading(false);
            return;
        }

        try {
            // Only save CIN and phone (RIB is saved via payment modal)
            await api.put('/confidential-data/me', {
                cin: confidentialFormData.cin,
                phone: confidentialFormData.phone
            });

            setSuccessMessage('Données confidentielles mises à jour avec succès');
            setShowSuccessModal(true);
            setShowEditConfidentialModal(false);
            fetchConfidentialData(); // Refresh the masked data
            if (profileIncomplete) {
                await refreshUser(); // Lift the profile gate once the phone is saved
            }
        } catch (error) {
            if (error.response?.data?.errors) {
                const firstError = Object.values(error.response.data.errors)[0][0];
                setError(firstError);
            } else {
                setError(error.response?.data?.message || 'Erreur lors de la mise à jour');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleChangePasswordClick = () => {
        setPasswordFormData({
                current_password: '',
                new_password: '',
                new_password_confirmation: ''
            });
        setShowCurrentPassword(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
        setError('');
        setShowChangePasswordModal(true);
    };

    const openEmailVerifyModal = () => {
        setEmailVerifyForm({ email: user?.email || '', code: '' });
        setEmailVerifyStep('request');
        setEmailVerifyError('');
        setEmailVerifyInfo('');
        setShowEmailVerifyModal(true);
    };

    const handleSendEmailCode = async (emailArg) => {
        setEmailVerifyLoading(true);
        setEmailVerifyError('');
        setEmailVerifyInfo('');

        const email = ((typeof emailArg === 'string' ? emailArg : emailVerifyForm.email) || '').trim();
        if (!email) {
            setEmailVerifyError('Veuillez saisir une adresse email.');
            setEmailVerifyLoading(false);
            return;
        }

        try {
            await api.post(`${emailVerifyBase}/email/send-code`, { email });
            setEmailVerifyStep('code');
            setEmailVerifyInfo(`Un code de vérification a été envoyé à ${email}. Vérifiez votre boîte mail. Si vous ne le trouvez pas, vérifiez vos spams.`);
        } catch (err) {
            if (err.response?.data?.errors) {
                const firstError = Object.values(err.response.data.errors)[0][0];
                setEmailVerifyError(firstError);
            } else {
                setEmailVerifyError(err.response?.data?.message || 'Erreur lors de l\'envoi du code.');
            }
        } finally {
            setEmailVerifyLoading(false);
        }
    };

    const handleVerifyEmailCode = async () => {
        setEmailVerifyLoading(true);
        setEmailVerifyError('');

        const email = (emailVerifyForm.email || '').trim();
        const code = (emailVerifyForm.code || '').trim();
        if (!code || code.length !== 6) {
            setEmailVerifyError('Le code de vérification doit contenir 6 chiffres.');
            setEmailVerifyLoading(false);
            return;
        }

        try {
            await api.post(`${emailVerifyBase}/email/verify`, { email, code });
            setShowEmailVerifyModal(false);
            setSuccessMessage('Adresse email vérifiée avec succès');
            setShowSuccessModal(true);
            await refreshUser(); // Lift the email gate / reflect the new email
        } catch (err) {
            if (err.response?.data?.errors) {
                const firstError = Object.values(err.response.data.errors)[0][0];
                setEmailVerifyError(firstError);
            } else {
                setEmailVerifyError(err.response?.data?.message || 'Code de vérification invalide ou expiré.');
            }
        } finally {
            setEmailVerifyLoading(false);
        }
    };

    // Send a verification code from inside the basic-info form (inline flow).
    const handleBasicSendCode = async () => {
        const email = (basicFormData.email || '').trim();
        if (!email) {
            setError('Veuillez saisir une adresse email valide.');
            return;
        }
        setBasicVerifyLoading(true);
        setError('');
        setBasicVerifyMsg('');
        try {
            await api.post(`${emailVerifyBase}/email/send-code`, { email });
            setBasicVerifyStage('sent');
            setBasicVerifyCode('');
            setBasicVerifyMsg(`Un code de vérification a été envoyé à ${email}. Vérifiez votre boîte mail. Si vous ne le trouvez pas, vérifiez vos spams.`);
        } catch (err) {
            if (err.response?.data?.errors) {
                setError(Object.values(err.response.data.errors)[0][0]);
            } else {
                setError(err.response?.data?.message || 'Erreur lors de l\'envoi du code.');
            }
        } finally {
            setBasicVerifyLoading(false);
        }
    };

    const handleSaveBasicInfo = async () => {
        setLoading(true);
        setError('');

        // Supervisors must provide a phone to unlock the app
        if (profileIncomplete && !basicFormData.phone) {
            setError('Vous devez renseigner votre numéro de téléphone pour accéder à l\'application.');
            setLoading(false);
            return;
        }

        // Validate phone if provided (for enqueteur, controleur, superviseur)
        if ((user?.role === 'enqueteur' || user?.role === 'controleur' || user?.role === 'superviseur') && basicFormData.phone) {
            if (basicFormData.phone.length >= 2) {
                const prefix = basicFormData.phone.substring(0, 2);
                if (!['05', '06', '07'].includes(prefix)) {
                    setError('Le numéro de téléphone doit commencer par 05, 06 ou 07');
                    setLoading(false);
                    return;
                }
            }
            if (basicFormData.phone.length !== 10 && basicFormData.phone.length > 0) {
                setError('Le numéro de téléphone doit contenir exactement 10 chiffres');
                setLoading(false);
                return;
            }
        }

        const targetEmail = (basicFormData.email || '').trim();
        // For supervisors, the email in the form must be verified before the save can
        // go through. It's "already verified" only when it equals the current email
        // AND that email is truly verified (not the sentinel / never confirmed).
        const emailAlreadyVerified = targetEmail === (user?.email || '') && user?.email_verified === true;
        const needsEmailVerification = hasProfileGate && !emailAlreadyVerified;

        if (needsEmailVerification) {
            if (!targetEmail) {
                setError('Veuillez saisir une adresse email valide.');
                setLoading(false);
                return;
            }
            // Step 1 — first click reveals the inline verification section.
            if (basicVerifyStage === 'none') {
                setBasicVerifyStage('prompt');
                setLoading(false);
                return;
            }
            // Step 2 — the section is shown but no code has been sent/entered yet.
            if (basicVerifyStage === 'prompt') {
                setError('Veuillez vérifier votre adresse email pour continuer (cliquez sur « Vérifier l\'adresse email »).');
                setLoading(false);
                return;
            }
            // Step 3 — a code was sent: verify it before saving the rest.
            const code = (basicVerifyCode || '').trim();
            if (!code || code.length !== 6) {
                setError('Veuillez saisir le code de vérification à 6 chiffres.');
                setLoading(false);
                return;
            }
            try {
                await api.post(`${emailVerifyBase}/email/verify`, { email: targetEmail, code });
            } catch (err) {
                if (err.response?.data?.errors) {
                    setError(Object.values(err.response.data.errors)[0][0]);
                } else {
                    setError(err.response?.data?.message || 'Code de vérification invalide ou expiré.');
                }
                setLoading(false);
                return;
            }
        }

        try {
            const updateData = {
                name: basicFormData.name,
            };
            // Ungated roles keep the legacy behaviour; a gated user's email is set
            // through the verification endpoint above (never sent here).
            if (!hasProfileGate) {
                updateData.email = basicFormData.email;
            }

            if (user.role === 'enqueteur') {
                await api.put('/interviewers/profile', updateData);
            } else if (user.role === 'controleur') {
                await api.put('/controllers/profile', updateData);
            } else if (user.role === 'superviseur') {
                await api.put('/supervisors/profile', updateData);
            } else {
                await api.put('/me/profile', updateData);
            }

            // Save CIN and phone to confidential data if applicable
            if (user?.role === 'enqueteur' || user?.role === 'controleur' || user?.role === 'superviseur') {
                await api.put('/confidential-data/me', {
                    cin: basicFormData.cin,
                    phone: basicFormData.phone
                });
                fetchConfidentialData(); // Refresh display
            }

            setSuccessMessage('Informations mises à jour avec succès');
            setShowSuccessModal(true);
            setShowEditBasicModal(false);
            setTimeout(() => window.location.reload(), 1500);
        } catch (error) {
            if (error.response?.data?.errors) {
                const firstError = Object.values(error.response.data.errors)[0][0];
                setError(firstError);
            } else {
                setError(error.response?.data?.message || 'Erreur lors de la mise à jour');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSavePaymentInfo = async () => {
        setLoading(true);
        setError('');

        // Validate CIN for all payment methods
        if (paymentFormData.payment_method_type) {
            if (!paymentFormData.cin || paymentFormData.cin.trim() === '') {
                setError('Le CIN est obligatoire pour cette méthode de paiement');
                setLoading(false);
                return;
            }
        }

        // Validate bank account number and bank if bank is selected
        if (paymentFormData.payment_method_type === 'bank') {
            if (!paymentFormData.bank_name || paymentFormData.bank_name === '') {
                setError('Veuillez sélectionner une banque');
                setLoading(false);
                return;
            }
            if (!paymentFormData.bank_account_number || paymentFormData.bank_account_number.trim() === '') {
                setError('Le numéro de compte bancaire (RIB ou numéro de compte chèque) est requis');
                setLoading(false);
                return;
            }
        }

        // Validate transfer service
        if (paymentFormData.payment_method_type === 'transfer_service') {
            if (!paymentFormData.transfer_service || paymentFormData.transfer_service === '') {
                setError('Veuillez sélectionner un service de transfert');
                setLoading(false);
                return;
            }
        }

        try {
            const updateData = {
                payment_method_type: paymentFormData.payment_method_type,
                payment_name: paymentFormData.payment_name || null
            };

            // Only include the relevant payment method field based on the selected type
            if (paymentFormData.payment_method_type === 'bank') {
                updateData.bank_name = paymentFormData.bank_name === 'Autre' ? paymentFormData.custom_bank_name : paymentFormData.bank_name;
                // Clear transfer_service when switching to bank
                updateData.transfer_service = null;
            } else if (paymentFormData.payment_method_type === 'transfer_service') {
                updateData.transfer_service = paymentFormData.transfer_service === 'Autre' ? paymentFormData.custom_transfer_service : paymentFormData.transfer_service;
                // Clear bank_name when switching to transfer
                updateData.bank_name = null;
            } else if (paymentFormData.payment_method_type === 'cash') {
                // Clear both bank_name and transfer_service for cash
                updateData.bank_name = null;
                updateData.transfer_service = null;
            }

            if (user.role === 'enqueteur') {
                await api.put('/interviewers/profile', updateData);
            } else if (user.role === 'controleur') {
                await api.put('/controllers/profile', updateData);
            } else if (user.role === 'superviseur') {
                await api.put('/supervisors/profile', updateData);
            }

            // Save CIN (and RIB for bank) to confidential data for all payment methods
            if (paymentFormData.payment_method_type) {
                const confidentialData = {
                    cin: paymentFormData.cin
                };
                // Add RIB only for bank payment
                if (paymentFormData.payment_method_type === 'bank' && paymentFormData.bank_account_number) {
                    confidentialData.bank_account_number = paymentFormData.bank_account_number;
                }
                await api.put('/confidential-data/me', confidentialData);
            }

            setSuccessMessage('Informations de paiement mises à jour avec succès');
            setShowSuccessModal(true);
            setShowEditPaymentModal(false);
            fetchConfidentialData(); // Refresh confidential data
            setTimeout(() => window.location.reload(), 1500);
        } catch (error) {
            if (error.response?.data?.errors) {
                const firstError = Object.values(error.response.data.errors)[0][0];
                setError(firstError);
            } else {
                setError(error.response?.data?.message || 'Erreur lors de la mise à jour');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSavePassword = async () => {
        setLoading(true);
        setError('');

        if (passwordFormData.new_password !== passwordFormData.new_password_confirmation) {
                    setError('Les nouveaux mots de passe ne correspondent pas');
                    setLoading(false);
                    return;
                }

        try {
            const updateData = {
                current_password: passwordFormData.current_password,
                new_password: passwordFormData.new_password,
                new_password_confirmation: passwordFormData.new_password_confirmation
            };

            if (user.role === 'enqueteur') {
                await api.put('/interviewers/profile', updateData);
            } else if (user.role === 'controleur') {
                await api.put('/controllers/profile', updateData);
            } else if (user.role === 'superviseur') {
                await api.put('/supervisors/profile', updateData);
            } else {
                await api.put('/me/profile', updateData);
            }

            setSuccessMessage('Mot de passe modifié avec succès');
            setShowSuccessModal(true);
            setShowChangePasswordModal(false);
            setTimeout(() => window.location.reload(), 1500);
        } catch (error) {
            if (error.response?.data?.errors) {
                const firstError = Object.values(error.response.data.errors)[0][0];
                setError(firstError);
            } else {
                setError(error.response?.data?.message || 'Erreur lors de la mise à jour');
            }
        } finally {
            setLoading(false);
        }
    };

    // Delegation functions for administrateur_general

    const fetchDelegationUsers = async () => {
        try {
            const response = await api.get('/payments/delegation-users');
            console.log('Delegation users response:', response.data);
            if (response.data.success) {
                console.log('Delegation users data:', response.data.data);
                setDelegationUsers(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching delegation users:', err);
        }
    };


    // Global delegation functions
    const fetchGlobalDelegationSettings = async () => {
        try {
            const response = await api.get('/payments/global-delegation-settings');
            if (response.data.success) {
                console.log('Global delegation settings response:', response.data.data);
                
                // Debug the data structure
                if (response.data.data) {
                    console.log('Raw data:', response.data.data);
                    console.log('Delegate 1 ID:', response.data.data.delegate_verification_to_1);
                    console.log('Delegate 2 ID:', response.data.data.delegate_verification_to_2);
                    console.log('Delegate 1 relationship:', response.data.data.delegateVerificationTo1);
                    console.log('Delegate 2 relationship:', response.data.data.delegateVerificationTo2);
                    console.log('All keys:', Object.keys(response.data.data));
                }
                
                setGlobalDelegationSettings(response.data.data);
                if (response.data.data) {
                    setGlobalDelegate1Id(response.data.data.delegate_verification_to_1 || '');
                    setGlobalDelegate2Id(response.data.data.delegate_verification_to_2 || '');
                }
            }
        } catch (err) {
            console.error('Error fetching global delegation settings:', err);
        }
    };

    const openGlobalDelegationModal = () => {
        console.log('Opening global delegation modal');
        console.log('Delegation users available:', delegationUsers);
        console.log('Delegation users length:', delegationUsers.length);
        
        if (globalDelegationSettings) {
            setGlobalDelegate1Id(globalDelegationSettings.delegate_verification_to_1 || '');
            setGlobalDelegate2Id(globalDelegationSettings.delegate_verification_to_2 || '');
        }
        setShowGlobalDelegationModal(true);
    };

    const handleGlobalDelegation = async () => {
        if (!globalDelegate1Id || !globalDelegate2Id) {
            setError('Veuillez sélectionner les deux délégués');
            return;
        }

        try {
            setProcessingGlobalDelegation(true);
            const response = await api.post('/payments/global-delegation-settings', {
                delegate_1_id: parseInt(globalDelegate1Id),
                delegate_2_id: parseInt(globalDelegate2Id),
            });

            if (response.data.success) {
                setSuccessMessage('Paramètres de délégation globale définis avec succès');
                setShowSuccessModal(true);
                setShowGlobalDelegationModal(false);
                fetchGlobalDelegationSettings();
            }
        } catch (err) {
            console.error('Error setting global delegation:', err);
            setError(err.response?.data?.message || 'Erreur lors de la définition des paramètres de délégation globale');
        } finally {
            setProcessingGlobalDelegation(false);
        }
    };

    const clearGlobalDelegation = () => {
        setShowDeleteConfirmationModal(true);
    };

    const handleConfirmDeleteDelegation = async () => {
        setShowDeleteConfirmationModal(false);
        setLoading(true);

        try {
            const response = await api.delete('/payments/global-delegation-settings');
            if (response.data.success) {
                setSuccessMessage('Paramètres de délégation globale supprimés avec succès');
                setShowSuccessModal(true);
                fetchGlobalDelegationSettings();
            }
        } catch (err) {
            console.error('Error clearing global delegation:', err);
            setError(err.response?.data?.message || 'Erreur lors de la suppression des paramètres de délégation globale');
        } finally {
            setLoading(false);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'active': return faCircleCheck;
            case 'suspended': return faPause;
            case 'inactive': return faStop;
            default: return faCircleXmark;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active': return 'text-green-600';
            case 'suspended': return 'text-red-600';
            case 'inactive': return 'text-yellow-600';
            default: return 'text-gray-600';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'active': return 'Actif';
            case 'suspended': return 'Suspendu';
            case 'inactive': return 'Inactif';
            default: return 'Inconnu';
        }
    };

    const getVerifiedIcon = (verified) => {
        if (verified === true || verified === 'yes') {
            return faCircleCheck;
        }
        return faCircleXmark;
    };

    const getVerifiedColor = (verified) => {
        if (verified === true || verified === 'yes') {
            return 'text-green-600';
        }
        return 'text-gray-400';
    };

    // Show loading state if user data is not available
    if (!user) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
                        backgroundSize: '20px 20px'
                    }}></div>
                </div>
                <div className="text-center relative z-10">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Chargement du profil...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 relative overflow-hidden py-8">
            {/* Dotted Background */}
            <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                    backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                }}></div>
            </div>

            {/* Blocked modal — shown when an incomplete supervisor tries to navigate away */}
            {showBlockedModal && (
                <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 text-center">
                        <div className="mx-auto mb-4 h-14 w-14 rounded-full bg-red-100 flex items-center justify-center">
                            <FontAwesomeIcon icon={faLock} className="text-2xl text-red-600" />
                        </div>
                        <h2 className="text-xl font-bold text-gray-900 mb-3">Accès restreint</h2>
                        <p className="text-gray-600 mb-3">
                            Veuillez compléter vos informations pour accéder au reste de l'application.
                        </p>
                        <p className="text-sm text-gray-500 mb-6">
                            Note : vous devez renseigner votre <strong>numéro de téléphone</strong> et
                            vérifier une <strong>adresse email valide</strong>.
                        </p>
                        {profileIncomplete ? (
                            <button
                                onClick={() => { setShowBlockedModal(false); handleEditBasicClick(); }}
                                className="w-full px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition-colors"
                            >
                                Compléter mon profil
                            </button>
                        ) : (
                            <button
                                onClick={() => { setShowBlockedModal(false); openEmailVerifyModal(); }}
                                className="w-full px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition-colors"
                            >
                                Vérifier mon adresse email
                            </button>
                        )}
                    </div>
                </div>
            )}

            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Header */}
                    <div className="mb-8">
                        <h1 className="text-4xl font-bold text-gray-900 mb-2">Mon Profil</h1>
                    <p className="text-gray-600">Consultez et gérez vos informations personnelles</p>
                    </div>

                {/* Self-service profile photo — prestataires and enquêteurs only */}
                {(user?.role === 'superviseur' || user?.role === 'enqueteur') && (
                    <ProfilePhotoUploader
                        personType={user.role === 'superviseur' ? 'supervisors' : 'interviewers'}
                        name={user?.name}
                        initialPhotoUpdatedAt={user?.photo_updated_at || null}
                    />
                )}

                {/* Profile completion required banner (incomplete supervisors) */}
                {profileIncomplete && (
                    <div className="mb-6 flex items-start gap-3 bg-red-50 border border-red-300 text-red-800 rounded-xl px-4 py-3 shadow-sm">
                        <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 text-red-600" />
                        <div>
                            <p className="font-semibold">Profil incomplet</p>
                            <p className="text-sm">Veuillez renseigner vos informations avant d'accéder au reste de l'application. Cliquez sur « Modifier » pour les compléter.</p>
                            <p className="text-sm">Note : vous devez au minimum renseigner votre <strong>numéro de téléphone</strong>.</p>
                        </div>
                    </div>
                )}

                {/* Email verification REQUIRED banner (new supervisors — hard blocked) */}
                {!profileIncomplete && emailVerificationRequired && (
                    <div className="mb-6 flex items-start gap-3 bg-red-50 border border-red-300 text-red-800 rounded-xl px-4 py-3 shadow-sm">
                        <FontAwesomeIcon icon={faEnvelope} className="mt-0.5 text-red-600" />
                        <div className="flex-1">
                            <p className="font-semibold">Adresse email non vérifiée</p>
                            <p className="text-sm">Veuillez vérifier votre adresse email avant d'accéder au reste de l'application. Vous pouvez conserver la même adresse ou la modifier — un code de vérification vous sera envoyé.</p>
                            <button
                                onClick={openEmailVerifyModal}
                                className="mt-2 inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
                            >
                                <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                                Vérifier mon adresse email
                            </button>
                        </div>
                    </div>
                )}

                {/* Email verification RECOMMENDED banner (existing supervisors — not blocked) */}
                {!profileIncomplete && !emailVerificationRequired && emailNotVerified && (
                    <div className="mb-6 flex items-start gap-3 bg-amber-50 border border-amber-300 text-amber-800 rounded-xl px-4 py-3 shadow-sm">
                        <FontAwesomeIcon icon={faEnvelope} className="mt-0.5 text-amber-600" />
                        <div className="flex-1">
                            <p className="font-semibold">Vérifiez votre adresse email</p>
                            <p className="text-sm">Pour sécuriser votre compte, veuillez confirmer votre adresse email. Vous pouvez conserver la même adresse ou la modifier — un code de vérification vous sera envoyé.</p>
                            <button
                                onClick={openEmailVerifyModal}
                                className="mt-2 inline-flex items-center px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium transition-colors"
                            >
                                <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                                Vérifier mon adresse email
                            </button>
                        </div>
                    </div>
                )}

                {/* Bento Grid Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column - 2/3 width */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Basic Information Card */}
                        <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-xl font-bold text-gray-900">
                                    Informations Personnelles
                                </h2>
                                <button
                                    onClick={handleEditBasicClick}
                                    className="flex items-center px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                                >
                                    <FontAwesomeIcon icon={faEdit} className="sm:mr-2" />
                                    <span className="hidden sm:inline">Modifier</span>
                                </button>
                            </div>
                            
                            <div className="space-y-3">
                                <div className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                    <FontAwesomeIcon icon={faUser} className="text-gray-400 text-lg mr-3 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs text-gray-500">Nom complet</p>
                                        <p className="text-base font-medium text-gray-900 break-words">{user.name}</p>
                                    </div>
                                </div>

                                {/* CIN - Only for Interviewers, Controllers, and Supervisors */}
                                {(user?.role === 'enqueteur' || user?.role === 'controleur' || user?.role === 'superviseur') && (
                                    <div className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                        <FontAwesomeIcon icon={faIdCard} className="text-gray-400 text-lg mr-3 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs text-gray-500">CIN</p>
                                            <p className="text-base font-medium text-gray-900 break-words">
                                                {loadingConfidential ? (
                                                    <FontAwesomeIcon icon={faSpinner} className="animate-spin text-gray-400" />
                                                ) : (
                                                    confidentialData?.cin || (confidentialData?.has_cin ? '********' : 'Non renseigné')
                                                )}
                                            </p>
                                        </div>
                                        {confidentialData?.has_cin && (
                                            <FontAwesomeIcon icon={faCheck} className="text-green-500 ml-2" />
                                        )}
                                    </div>
                                )}

                                {/* Phone - Only for Interviewers, Controllers, and Supervisors */}
                                {(user?.role === 'enqueteur' || user?.role === 'controleur' || user?.role === 'superviseur') && (
                                    <div className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                        <FontAwesomeIcon icon={faPhone} className="text-gray-400 text-lg mr-3 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs text-gray-500">Téléphone</p>
                                            <p className="text-base font-medium text-gray-900 break-words">
                                                {loadingConfidential ? (
                                                    <FontAwesomeIcon icon={faSpinner} className="animate-spin text-gray-400" />
                                                ) : (
                                                    confidentialData?.phone || (confidentialData?.has_phone ? '********' : 'Non renseigné')
                                                )}
                                            </p>
                                        </div>
                                        {confidentialData?.has_phone && (
                                            <FontAwesomeIcon icon={faCheck} className="text-green-500 ml-2" />
                                        )}
                                    </div>
                                )}

                                <div className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                    <FontAwesomeIcon icon={faEnvelope} className="text-gray-400 text-lg mr-3 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs text-gray-500">Email</p>
                                        <p className="text-base font-medium text-gray-900 break-words break-all">{user.email}</p>
                                    </div>
                                    {hasProfileGate && (
                                        user?.email_verified ? (
                                            <FontAwesomeIcon icon={faCheck} className="text-green-500 ml-2 flex-shrink-0" title="Email vérifié" />
                                        ) : (
                                            <FontAwesomeIcon icon={faCircleExclamation} className="text-amber-500 ml-2 flex-shrink-0" title="Email non vérifié" />
                                        )
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Delegation Section for Administrateur General */}
                        {user?.role === 'administrateur_general' && (
                            <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-xl border border-gray-100">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
                                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center">
                                        <FontAwesomeIcon icon={faUserCheck} className="mr-2 text-red-600 flex-shrink-0" />
                                        <span className="break-words">Délégation de Vérification</span>
                                    </h3>
                                    <button
                                        onClick={openGlobalDelegationModal}
                                        className="w-full sm:w-auto px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center justify-center sm:justify-start"
                                    >
                                        <FontAwesomeIcon icon={faUserPlus} className="mr-1 sm:mr-2 flex-shrink-0" />
                                        <span className="whitespace-nowrap">Paramètres Globaux</span>
                                    </button>
                                </div>

                                {/* Global Delegation Status */}
                                {globalDelegationSettings ? (
                                    <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-white border border-red-200 rounded-xl">
                                        <div className="flex flex-col gap-3 sm:gap-4">
                                            <div className="flex items-center mb-2 sm:mb-3">
                                                <FontAwesomeIcon icon={faCircleCheck} className="text-red-600 mr-2 flex-shrink-0" />
                                                <p className="text-xs sm:text-sm font-semibold text-gray-800 break-words">Délégation globale active</p>
                                            </div>
                                            <div className="space-y-3 sm:space-y-2">
                                                <div className="flex flex-col gap-2">
                                                    <span className="text-xs sm:text-sm font-medium text-gray-700">Délégué 1:</span>
                                                    <div className="flex flex-wrap items-center gap-2 min-w-0">
                                                        <span className="text-xs sm:text-sm font-medium break-words min-w-0">
                                                            {globalDelegationSettings.delegate_1_user?.name || 
                                                             globalDelegationSettings.delegateVerificationTo1?.name || 
                                                             globalDelegationSettings.delegate_verification_to_1?.name || 'N/A'}
                                                        </span>
                                                        <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full flex-shrink-0 whitespace-nowrap">
                                                            {globalDelegationSettings.delegate_1_user?.role || 
                                                             globalDelegationSettings.delegateVerificationTo1?.role || 
                                                             globalDelegationSettings.delegate_verification_to_1?.role || 'N/A'}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col gap-2">
                                                    <span className="text-xs sm:text-sm font-medium text-gray-700">Délégué 2:</span>
                                                    <div className="flex flex-wrap items-center gap-2 min-w-0">
                                                        <span className="text-xs sm:text-sm font-medium break-words min-w-0">
                                                            {globalDelegationSettings.delegate_2_user?.name || 
                                                             globalDelegationSettings.delegateVerificationTo2?.name || 
                                                             globalDelegationSettings.delegate_verification_to_2?.name || 'N/A'}
                                                        </span>
                                                        <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full flex-shrink-0 whitespace-nowrap">
                                                            {globalDelegationSettings.delegate_2_user?.role || 
                                                             globalDelegationSettings.delegateVerificationTo2?.role || 
                                                             globalDelegationSettings.delegate_verification_to_2?.role || 'N/A'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                onClick={clearGlobalDelegation}
                                                className="w-full sm:w-auto self-start px-3 py-1.5 sm:py-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors whitespace-nowrap"
                                            >
                                                Supprimer
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-white border border-red-200 rounded-xl">
                                        <div className="flex items-start">
                                            <FontAwesomeIcon icon={faCircleExclamation} className="text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                                            <p className="text-xs sm:text-sm text-gray-800 break-words min-w-0">
                                                Aucune délégation globale définie. Les nouveaux lots n'auront pas de délégation automatique.
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Quick Actions */}
                                <div className="pt-4 sm:pt-6">
                                    <p className="text-xs sm:text-sm text-gray-600 mb-3">
                                        Gérez les lots en attente de vérification depuis la page dédiée.
                                    </p>
                                    <button
                                        onClick={() => window.open('/payments/verification', '_blank')}
                                        className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm"
                                    >
                                        <FontAwesomeIcon icon={faEye} className="mr-2" />
                                        Voir les Lots en Attente
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Payment Information Card - For Interviewers, Controllers, and Supervisors */}
                        {(user?.role === 'enqueteur' || user?.role === 'controleur' || user?.role === 'superviseur') && (
                            <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100">
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-xl font-bold text-gray-900">
                                        Informations de Paiement
                                    </h2>
                                    <button
                                        onClick={handleEditPaymentClick}
                                        className="flex items-center px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                                    >
                                        <FontAwesomeIcon icon={faEdit} className="sm:mr-2" />
                                        <span className="hidden sm:inline">Modifier</span>
                                    </button>
                                </div>
                                
                                <div className="space-y-3">
                                    <div className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                        <FontAwesomeIcon icon={faCreditCard} className="text-gray-400 text-lg mr-3 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs text-gray-500">Méthode de paiement</p>
                                            <p className="text-base font-medium text-gray-900 break-words">
                                                {user?.payment_method_type === 'bank' ? 'Versement bancaire' :
                                                 user?.payment_method_type === 'transfer_service' ? 'Service de Transfert' :
                                                 user?.payment_method_type === 'cash' ? 'Espèces' :
                                                 'Non configuré'}
                                            </p>
                                        </div>
                                    </div>
                                    
                                    {user?.payment_method_type && (
                                        <div className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                            <FontAwesomeIcon icon={faUser} className="text-gray-400 text-lg mr-3 flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-gray-500">Nom pour le paiement</p>
                                                <p className="text-base font-medium text-gray-900 break-words">{user.payment_name || user.name}</p>
                                            </div>
                                        </div>
                                    )}

                                    {user?.payment_method_type === 'bank' && user?.bank_name && (
                                        <div className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                            <FontAwesomeIcon icon={faUniversity} className="text-blue-600 text-lg mr-3 flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-gray-500">Banque</p>
                                                <p className="text-base font-medium text-gray-900 break-words">{user.bank_name}</p>
                                            </div>
                                        </div>
                                    )}

                                    {user?.payment_method_type === 'bank' && (
                                        <div className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                            <FontAwesomeIcon icon={faIdCard} className="text-blue-600 text-lg mr-3 flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-gray-500">RIB (Numéro de compte)</p>
                                                <p className="text-base font-medium text-gray-900 break-words">
                                                    {loadingConfidential ? (
                                                        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-gray-400" />
                                                    ) : (
                                                        confidentialData?.bank_account_number || (confidentialData?.has_bank_account_number ? '********' : 'Non renseigné')
                                                    )}
                                                </p>
                                            </div>
                                            {confidentialData?.has_bank_account_number && (
                                                <FontAwesomeIcon icon={faCheck} className="text-green-500 ml-2" />
                                            )}
                                        </div>
                                    )}
                                    
                                    {user?.payment_method_type === 'transfer_service' && user?.transfer_service && (
                                        <div className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                            <FontAwesomeIcon icon={faMoneyBillTransfer} className="text-green-600 text-lg mr-3 flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-gray-500">Service de transfert</p>
                                                <p className="text-base font-medium text-gray-900 break-words">{user.transfer_service}</p>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {user?.payment_method_type === 'cash' && (
                                        <div className="flex items-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                                            <FontAwesomeIcon icon={faCircleExclamation} className="text-blue-600 text-lg mr-3" />
                                            <div className="flex-1">
                                                <p className="text-xs text-blue-700">Information</p>
                                                <p className="text-sm font-medium text-blue-900">
                                                    Vous devez vous rendre chez Immersion pour recevoir votre paiement en espèces.
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {!user?.payment_method_type && (
                                        <div className="text-center py-6">
                                            <FontAwesomeIcon icon={faCircleExclamation} className="text-3xl text-gray-300 mb-2" />
                                            <p className="text-gray-500 text-sm">Aucune méthode configurée</p>
                                            <p className="text-xs text-gray-400 mt-1">Cliquez sur "Modifier" pour ajouter</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                                )}

                        
                        {/* Completed Formations Card - Only for Interviewers, Controllers, and Supervisors */}
                        {(user?.role === 'enqueteur' || user?.role === 'controleur' || user?.role === 'superviseur') && (
                            <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100">
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-xl font-bold text-gray-900 flex items-center">
                                        <FontAwesomeIcon icon={faBullhorn} className="mr-2 text-red-600" />
                                        Formations Complétées
                                    </h2>
                                </div>
                                
                                {loadingFormations ? (
                                    <div className="flex justify-center items-center py-8">
                                        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-red-600 text-2xl" />
                                    </div>
                                ) : completedFormations.length === 0 ? (
                                    <div className="text-center py-6">
                                        <FontAwesomeIcon icon={faBullhorn} className="text-3xl text-gray-300 mb-2" />
                                        <p className="text-gray-500 text-sm">Aucune formation complétée</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {completedFormations.map((formation) => (
                                            <div key={formation.id} className="flex items-start p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200">
                                                <div className="flex-shrink-0 mr-4">
                                                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                                        <FontAwesomeIcon icon={faCheck} className="text-green-600" />
                                                    </div>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="text-base font-semibold text-gray-900 mb-1 break-words">
                                                        {formation.title}
                                                    </h3>
                                                    {formation.description && (
                                                        <p className="text-sm text-gray-600 mb-2 line-clamp-2 break-words">
                                                            {formation.description}
                                                        </p>
                                                    )}
                                                    <div className="flex items-center text-xs text-gray-500">
                                                        <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
                                                        <span>Complétée le {new Date(formation.completed_at).toLocaleDateString('fr-FR', { 
                                                            year: 'numeric', 
                                                            month: 'long', 
                                                            day: 'numeric' 
                                                        })}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                            </div>

                    {/* Right Column - 1/3 width */}
                    <div className="space-y-6">
                        {/* Account Status Card */}
                        <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100">
                            <h3 className="text-xl font-bold text-gray-900 mb-4">
                                Statut du Compte
                            </h3>
                            <div className="space-y-3">
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <span className="text-gray-600 text-xs">Statut</span>
                                    <div className="flex items-center space-x-2">
                                        <FontAwesomeIcon 
                                            icon={getStatusIcon(user?.status)} 
                                            className={getStatusColor(user?.status)}
                                        />
                                        <span className="font-medium text-sm">{getStatusText(user?.status)}</span>
                                    </div>
                                </div>
                                
                                {user?.role === 'enqueteur' && (
                                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                        <span className="text-gray-600 text-xs">Vérification</span>
                                        <div className="flex items-center space-x-2">
                                            <FontAwesomeIcon 
                                                icon={getVerifiedIcon(user?.verified)} 
                                                className={getVerifiedColor(user?.verified)}
                                            />
                                            <span className="font-medium text-sm">
                                                {user?.verified === 'yes' || user?.verified === true ? 'Vérifié' : 'Non vérifié'}
                                            </span>
                                        </div>
                                </div>
                            )}

                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <span className="text-gray-600 text-xs">Rôle</span>
                                    <span className="font-medium text-sm">{user?.role_display || user?.role}</span>
                                </div>
                                
                                {user?.ref && (
                                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                        <span className="text-gray-600 text-xs">Référence</span>
                                        <span className="font-mono font-medium text-sm">{user?.ref}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Security Card */}
                        <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-xl border border-gray-100">
                            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">
                                Sécurité
                            </h3>
                            <div className="space-y-3 mb-4">
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <span className="text-gray-600 text-xs">Mot de passe</span>
                                    <span className="text-sm font-medium text-green-700">Sécurisé</span>
                                </div>
                            </div>
                            <button
                                onClick={handleChangePasswordClick}
                                className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm"
                            >
                                <FontAwesomeIcon icon={faKey} className="mr-2" />
                                Changer le Mot de Passe
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Edit Basic Info Modal */}
            {showEditBasicModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h3 className="text-2xl font-bold text-gray-900">
                                    <FontAwesomeIcon icon={faUser} className="mr-2 text-red-600" />
                                    Modifier les Informations
                                </h3>
                                <button
                                    onClick={() => setShowEditBasicModal(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <FontAwesomeIcon icon={faTimes} className="text-xl" />
                                </button>
                            </div>
                        </div>
                        
                        <div className="p-6">
                            {error && (
                                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-red-700 text-sm">{error}</p>
                                </div>
                            )}

                            <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Nom complet
                                        </label>
                                        <div className="relative">
                                            <FontAwesomeIcon
                                                icon={faUser}
                                                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                            />
                                            <input
                                                type="text"
                                                value={basicFormData.name}
                                                onChange={(e) => setBasicFormData({...basicFormData, name: e.target.value})}
                                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                                            />
                                        </div>
                                    </div>

                                    {/* CIN - Only for Interviewers, Controllers, and Supervisors */}
                                    {(user?.role === 'enqueteur' || user?.role === 'controleur' || user?.role === 'superviseur') && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                CIN (Carte d'Identité Nationale)
                                            </label>
                                            <div className="relative">
                                                <FontAwesomeIcon
                                                    icon={faIdCard}
                                                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                                />
                                                <input
                                                    type="text"
                                                    value={basicFormData.cin}
                                                    onChange={(e) => {
                                                        const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                                                        setBasicFormData({...basicFormData, cin: value});
                                                    }}
                                                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none uppercase"
                                                    placeholder="Ex: AB123456"
                                                    maxLength={20}
                                                />
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">Lettres et chiffres uniquement</p>
                                        </div>
                                    )}

                                    {/* Phone - Only for Interviewers, Controllers, and Supervisors */}
                                    {(user?.role === 'enqueteur' || user?.role === 'controleur' || user?.role === 'superviseur') && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Numéro de téléphone
                                            </label>
                                            <div className="relative">
                                                <FontAwesomeIcon
                                                    icon={faPhone}
                                                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                                />
                                                <input
                                                    type="tel"
                                                    value={basicFormData.phone}
                                                    onChange={(e) => {
                                                        const value = e.target.value.replace(/[^0-9]/g, '');
                                                        setBasicFormData({...basicFormData, phone: value});
                                                    }}
                                                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none ${
                                                        basicFormData.phone && basicFormData.phone.length >= 2 && !['05', '06', '07'].includes(basicFormData.phone.substring(0, 2))
                                                            ? 'border-red-500'
                                                            : 'border-gray-300'
                                                    }`}
                                                    placeholder="Ex: 0612345678"
                                                    maxLength={10}
                                                />
                                            </div>
                                            {basicFormData.phone && basicFormData.phone.length >= 2 && !['05', '06', '07'].includes(basicFormData.phone.substring(0, 2)) ? (
                                                <p className="text-xs text-red-600 mt-1">Le numéro doit commencer par 05, 06 ou 07</p>
                                            ) : (
                                                <p className="text-xs text-gray-500 mt-1">10 chiffres, commence par 05, 06 ou 07</p>
                                            )}
                                        </div>
                                    )}

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Email
                                        </label>
                                        <div className="relative">
                                            <FontAwesomeIcon
                                                icon={faEnvelope}
                                                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                            />
                                            {hasProfileGate ? (
                                                <input
                                                    type="email"
                                                    value={basicFormData.email}
                                                    onChange={(e) => setBasicFormData({ ...basicFormData, email: e.target.value })}
                                                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                                                    placeholder="exemple@email.com"
                                                />
                                            ) : (
                                                <input
                                                    type="email"
                                                    value={basicFormData.email}
                                                    readOnly
                                                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed outline-none"
                                                />
                                            )}
                                        </div>
                                        {hasProfileGate && basicVerifyStage === 'none' && (
                                            <p className="text-xs text-gray-500 mt-1">
                                                Si votre adresse email n'est pas vérifiée, un code de vérification vous sera demandé lors de l'enregistrement.
                                            </p>
                                        )}
                                    </div>

                                    {/* Inline email verification section (supervisors + F2F interviewers) */}
                                    {hasProfileGate && basicVerifyStage !== 'none' && (
                                        <div className="border border-amber-200 rounded-lg p-4 bg-amber-50 space-y-3">
                                            {basicVerifyMsg && (
                                                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                                                    <p className="text-green-700 text-sm">{basicVerifyMsg}</p>
                                                </div>
                                            )}

                                            {basicVerifyStage === 'prompt' ? (
                                                <>
                                                    <div className="flex items-start">
                                                        <FontAwesomeIcon icon={faCircleExclamation} className="text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
                                                        <p className="text-sm text-amber-800">
                                                            Votre adresse email n'est pas vérifiée. Souhaitez-vous la vérifier maintenant ? Un code de vérification sera envoyé à <strong className="break-all">{(basicFormData.email || '').trim()}</strong>.
                                                        </p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={handleBasicSendCode}
                                                        disabled={basicVerifyLoading}
                                                        className="w-full flex items-center justify-center px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                                                    >
                                                        {basicVerifyLoading ? (
                                                            <>
                                                                <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                                                                Envoi...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                                                                Vérifier l'adresse email
                                                            </>
                                                        )}
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <label className="block text-sm font-medium text-gray-700">
                                                        Code de vérification
                                                    </label>
                                                    <div className="relative">
                                                        <FontAwesomeIcon
                                                            icon={faKey}
                                                            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                                        />
                                                        <input
                                                            type="text"
                                                            inputMode="numeric"
                                                            value={basicVerifyCode}
                                                            onChange={(e) => setBasicVerifyCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                                                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none tracking-[0.5em] text-center text-lg bg-white"
                                                            placeholder="______"
                                                            maxLength={6}
                                                        />
                                                    </div>
                                                    <p className="text-xs text-gray-500">
                                                        Saisissez le code puis cliquez sur « Enregistrer » pour valider.
                                                    </p>
                                                    <button
                                                        type="button"
                                                        onClick={handleBasicSendCode}
                                                        disabled={basicVerifyLoading}
                                                        className="text-sm text-amber-700 hover:text-amber-900 transition-colors"
                                                    >
                                                        {basicVerifyLoading ? 'Envoi...' : 'Renvoyer un code'}
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        
                        <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
                            <button
                                onClick={() => setShowEditBasicModal(false)}
                                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleSaveBasicInfo}
                                disabled={loading}
                                className="flex items-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {loading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Sauvegarde...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faSave} className="mr-2" />
                                        Enregistrer
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Payment Info Modal */}
            {showEditPaymentModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h3 className="text-2xl font-bold text-gray-900">
                                    <FontAwesomeIcon icon={faCreditCard} className="mr-2 text-green-600" />
                                    Modifier les Informations de Paiement
                                </h3>
                                <button
                                    onClick={() => setShowEditPaymentModal(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <FontAwesomeIcon icon={faTimes} className="text-xl" />
                                </button>
                            </div>
                        </div>
                        
                        <div className="p-6">
                            {error && (
                                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-red-700 text-sm">{error}</p>
                                </div>
                            )}
                            
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Type de méthode de paiement
                                    </label>
                                    <select
                                        value={paymentFormData.payment_method_type}
                                        onChange={(e) => {
                                            const newPaymentType = e.target.value;
                                            setPaymentFormData(prev => ({
                                                ...prev,
                                                payment_method_type: newPaymentType,
                                                // Clear the old payment method fields when switching
                                                bank_name: newPaymentType === 'bank' ? prev.bank_name : '',
                                                transfer_service: newPaymentType === 'transfer_service' ? prev.transfer_service : '',
                                                bank_account_number: newPaymentType === 'bank' ? prev.bank_account_number : '',
                                                custom_bank_name: '',
                                                custom_transfer_service: ''
                                            }));
                                        }}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                                    >
                                        <option value="">Sélectionner une méthode</option>
                                        <option value="bank">Versement bancaire</option>
                                        <option value="transfer_service">Service de Transfert</option>
                                        <option value="cash">Espèces</option>
                                    </select>
                                    
                                    {/* Info message for bank and transfer */}
                                    {(paymentFormData.payment_method_type === 'bank' || paymentFormData.payment_method_type === 'transfer_service') && (
                                        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                                            <div className="flex items-start">
                                                <FontAwesomeIcon icon={faCircleExclamation} className="text-amber-600 text-sm mr-2 mt-0.5 flex-shrink-0" />
                                                <p className="text-xs text-amber-800">
                                                    <span className="font-medium">Note importante :</span> Les frais de {paymentFormData.payment_method_type === 'bank' ? 'versement' : 'transfert'} sont à votre charge et seront déduits du montant total de votre paiement.
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Verification Section for all payment methods */}
                                {paymentFormData.payment_method_type && (
                                    <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                                        <div className="flex items-center mb-3">
                                            <FontAwesomeIcon icon={faUserCheck} className="text-blue-600 mr-2" />
                                            <h4 className="text-sm font-semibold text-blue-900">Vérification des informations personnelles</h4>
                                        </div>
                                        <p className="text-xs text-blue-700 mb-4">
                                            Veuillez vérifier et mettre à jour vos informations si nécessaire. Ces informations seront utilisées pour le paiement.
                                        </p>

                                        {/* Payment Name */}
                                        <div className="mb-3">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Nom pour le paiement
                                            </label>
                                            <input
                                                type="text"
                                                value={paymentFormData.payment_name}
                                                onChange={(e) => setPaymentFormData({...paymentFormData, payment_name: e.target.value})}
                                                placeholder={user?.name || 'Nom du bénéficiaire'}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none bg-white"
                                            />
                                            <p className="text-xs text-gray-500 mt-1">
                                            Veuillez entrer votre nom tel qu'il apparaît sur votre compte. Notez que le paiement ne passera pas si vous utilisez le compte d'une autre personne.
                                            </p>
                                        </div>

                                        {/* CIN Verification */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                CIN (Carte d'Identité Nationale) <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={paymentFormData.cin}
                                                onChange={(e) => {
                                                    // Allow only alphanumeric and convert to uppercase
                                                    const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                                                    setPaymentFormData({...paymentFormData, cin: value});
                                                }}
                                                placeholder="Ex: AB123456"
                                                maxLength={20}
                                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none bg-white ${
                                                    !paymentFormData.cin ? 'border-amber-500' : 'border-gray-300'
                                                }`}
                                            />
                                            <p className="text-xs text-gray-500 mt-1">
                                                {paymentFormData.cin
                                                    ? 'Vérifiez que votre CIN est correct'
                                                    : 'Le CIN est obligatoire pour le paiement'}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Bank Information */}
                                {paymentFormData.payment_method_type === 'bank' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Banque <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            value={paymentFormData.bank_name}
                                            onChange={(e) => setPaymentFormData({...paymentFormData, bank_name: e.target.value})}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                                        >
                                            <option value="">Sélectionner une banque</option>
                                            <option value="Al Akhdar Bank">Al Akhdar Bank (AAB)</option>
                                            <option value="Al Barid Bank">Al Barid Bank</option>
                                            <option value="Arab Bank PLC">Arab Bank PLC</option>
                                            <option value="Attijariwafa Bank">Attijariwafa Bank</option>
                                            <option value="Banco Sabadell">Banco Sabadell</option>
                                            <option value="Bank Al-Amal">Bank Al-Amal</option>
                                            <option value="Bank Al Yousr">Bank Al Yousr</option>
                                            <option value="Bank Al-Tamweel wa Al-Inma">Bank Al-Tamweel wa Al-Inma</option>
                                            <option value="Bank As-Safa">Bank As-Safa</option>
                                            <option value="Bank of Africa">Bank of Africa</option>
                                            <option value="Banque Populaire">Banque Populaire</option>
                                            <option value="BMCI">Banque Marocaine pour le Commerce et l'Industrie (BMCI)</option>
                                            <option value="Caixa Bank">Caixa Bank</option>
                                            <option value="CIH Bank">CIH Bank</option>
                                            <option value="Citibank Morocco">Citibank Morocco</option>
                                            <option value="Crédit Agricole du Maroc">Crédit Agricole du Maroc (CAM)</option>
                                            <option value="Crédit du Maroc">Crédit du Maroc</option>
                                            <option value="Umnia Bank">Umnia Bank</option>
                                            <option value="Autre">Autre</option>
                                        </select>
                                        
                                        {/* Custom Bank Name Input */}
                                        {paymentFormData.bank_name === 'Autre' && (
                                            <div className="mt-3">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Nom de la banque
                                                </label>
                                                <input
                                                    type="text"
                                                    value={paymentFormData.custom_bank_name}
                                                    onChange={(e) => setPaymentFormData({...paymentFormData, custom_bank_name: e.target.value})}
                                                    placeholder="Entrez le nom de votre banque"
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                                                />
                                            </div>
                                        )}

                                        {/* RIB / Bank account number - Required for bank payment */}
                                        <div className="mt-3">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                RIB / Numéro de compte bancaire <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={paymentFormData.bank_account_number}
                                                onChange={(e) => setPaymentFormData({...paymentFormData, bank_account_number: e.target.value})}
                                                placeholder="RIB ou numéro de compte chèque (caractères et chiffres)"
                                                maxLength={100}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                                            />
                                            <p className="text-xs text-gray-500 mt-1">
                                                RIB (24 chiffres) ou numéro de compte chèque — caractères et chiffres acceptés
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Transfer Service Information */}
                                {paymentFormData.payment_method_type === 'transfer_service' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Service de transfert
                                        </label>
                                        <select
                                            value={paymentFormData.transfer_service}
                                            onChange={(e) => setPaymentFormData({...paymentFormData, transfer_service: e.target.value})}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                                        >
                                            <option value="">Sélectionner un service</option>
                                            <option value="Barid Cash">Barid Cash</option>
                                            <option value="Wafacash">Wafacash</option>
                                            <option value="Western Union">Western Union</option>
                                            <option value="MoneyGram">MoneyGram</option>
                                            <option value="Cash Plus">Cash Plus</option>
                                            <option value="Autre">Autre</option>
                                        </select>
                                        
                                        {/* Custom Transfer Service Input */}
                                        {paymentFormData.transfer_service === 'Autre' && (
                                            <div className="mt-3">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Nom du service de transfert
                                                </label>
                                                <input
                                                    type="text"
                                                    value={paymentFormData.custom_transfer_service}
                                                    onChange={(e) => setPaymentFormData({...paymentFormData, custom_transfer_service: e.target.value})}
                                                    placeholder="Entrez le nom du service"
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Cash Payment Information */}
                                {paymentFormData.payment_method_type === 'cash' && (
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                        <div className="flex items-start">
                                            <FontAwesomeIcon icon={faCircleExclamation} className="text-blue-600 text-lg mr-3 mt-1" />
                                            <div>
                                                <p className="text-sm font-medium text-blue-900 mb-1">
                                                    Paiement en Espèces
                                                </p>
                                                <p className="text-sm text-blue-700">
                                                    Vous devez vous rendre chez Immersion pour recevoir votre paiement en espèces.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
                            <button
                                onClick={() => setShowEditPaymentModal(false)}
                                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleSavePaymentInfo}
                                disabled={loading}
                                className="flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {loading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Sauvegarde...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faSave} className="mr-2" />
                                        Enregistrer
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Change Password Modal */}
            {showChangePasswordModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h3 className="text-2xl font-bold text-gray-900">
                                    Changer le Mot de Passe
                                </h3>
                                <button
                                    onClick={() => setShowChangePasswordModal(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <FontAwesomeIcon icon={faTimes} className="text-xl" />
                                </button>
                                    </div>
                                </div>

                        <div className="p-6">
                            {error && (
                                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-red-700 text-sm">{error}</p>
                                </div>
                            )}
                            
                            <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Mot de passe actuel
                                                </label>
                                                <div className="relative">
                                                    <FontAwesomeIcon
                                                        icon={faKey}
                                                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                                    />
                                                    <input
                                                        type={showCurrentPassword ? "text" : "password"}
                                            value={passwordFormData.current_password}
                                            onChange={(e) => setPasswordFormData({...passwordFormData, current_password: e.target.value})}
                                                        className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                                            placeholder="Entrez votre mot de passe actuel"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                                    >
                                                        <FontAwesomeIcon icon={showCurrentPassword ? faEyeSlash : faEye} />
                                                    </button>
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Nouveau mot de passe
                                                </label>
                                                <div className="relative">
                                                    <FontAwesomeIcon
                                                        icon={faKey}
                                                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                                    />
                                                    <input
                                                        type={showNewPassword ? "text" : "password"}
                                            value={passwordFormData.new_password}
                                            onChange={(e) => setPasswordFormData({...passwordFormData, new_password: e.target.value})}
                                                        className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                                            placeholder="Nouveau mot de passe"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                                    >
                                                        <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} />
                                                    </button>
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Confirmer le mot de passe
                                                </label>
                                                <div className="relative">
                                                    <FontAwesomeIcon
                                                        icon={faKey}
                                                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                                    />
                                                    <input
                                                        type={showConfirmPassword ? "text" : "password"}
                                            value={passwordFormData.new_password_confirmation}
                                            onChange={(e) => setPasswordFormData({...passwordFormData, new_password_confirmation: e.target.value})}
                                                        className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                                            placeholder="Confirmez le nouveau mot de passe"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                                    >
                                                        <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                        <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
                                        <button
                                onClick={() => setShowChangePasswordModal(false)}
                                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                                        >
                                            Annuler
                                        </button>
                                        <button
                                onClick={handleSavePassword}
                                            disabled={loading}
                                            className="flex items-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            {loading ? (
                                                <>
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                    Sauvegarde...
                                                </>
                                            ) : (
                                                <>
                                                    <FontAwesomeIcon icon={faSave} className="mr-2" />
                                        Enregistrer
                                                </>
                                            )}
                                        </button>
                                    </div>
                                            </div>
                                        </div>
                                    )}

            {/* Edit Personal Data Modal */}
            {showEditConfidentialModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h3 className="text-2xl font-bold text-gray-900">
                                    Modifier les Données Personnelles
                                </h3>
                                <button
                                    onClick={() => setShowEditConfidentialModal(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <FontAwesomeIcon icon={faTimes} className="text-xl" />
                                </button>
                            </div>
                        </div>

                        <div className="p-6">
                            {error && (
                                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-red-700 text-sm">{error}</p>
                                </div>
                            )}

                            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                                <div className="flex items-start">
                                    <FontAwesomeIcon icon={faShieldAlt} className="text-red-600 text-lg mr-3 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-medium text-red-900 mb-1">
                                            Sécurité des données
                                        </p>
                                        <p className="text-sm text-red-700">
                                            Ces informations sont chiffrées et stockées sur un serveur sécurisé séparé.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        CIN (Carte d'Identité Nationale)
                                        {user?.payment_method_type && (
                                            <span className="text-red-500 ml-1">*</span>
                                        )}
                                    </label>
                                    <div className="relative">
                                        <FontAwesomeIcon
                                            icon={faIdCard}
                                            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                        />
                                        <input
                                            type="text"
                                            value={confidentialFormData.cin}
                                            onChange={(e) => {
                                                // Allow only alphanumeric characters
                                                const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                                                setConfidentialFormData({...confidentialFormData, cin: value});
                                            }}
                                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none uppercase"
                                            placeholder="Ex: AB123456"
                                            maxLength={20}
                                        />
                                    </div>
                                    {user?.payment_method_type && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            Requis pour le paiement (lettres et chiffres uniquement)
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Numéro de téléphone
                                    </label>
                                    <div className="relative">
                                        <FontAwesomeIcon
                                            icon={faPhone}
                                            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                        />
                                        <input
                                            type="tel"
                                            value={confidentialFormData.phone}
                                            onChange={(e) => {
                                                // Allow only digits
                                                const value = e.target.value.replace(/[^0-9]/g, '');
                                                setConfidentialFormData({...confidentialFormData, phone: value});
                                            }}
                                            className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none ${
                                                confidentialFormData.phone && confidentialFormData.phone.length >= 2 && !['05', '06', '07'].includes(confidentialFormData.phone.substring(0, 2))
                                                    ? 'border-red-500'
                                                    : 'border-gray-300'
                                            }`}
                                            placeholder="Ex: 0612345678"
                                            maxLength={10}
                                        />
                                    </div>
                                    {confidentialFormData.phone && confidentialFormData.phone.length >= 2 && !['05', '06', '07'].includes(confidentialFormData.phone.substring(0, 2)) ? (
                                        <p className="text-xs text-red-600 mt-1">
                                            Le numéro doit commencer par 05, 06 ou 07
                                        </p>
                                    ) : (
                                        <p className="text-xs text-gray-500 mt-1">
                                            10 chiffres, commence par 05, 06 ou 07
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
                            <button
                                onClick={() => setShowEditConfidentialModal(false)}
                                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleSaveConfidentialData}
                                disabled={loading}
                                className="flex items-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {loading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Sauvegarde...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faSave} className="mr-2" />
                                        Enregistrer
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Global Delegation Modal */}
            {showGlobalDelegationModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-4">
                    <div className="relative top-10 sm:top-20 mx-auto p-4 sm:p-5 border w-full max-w-md sm:w-96 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
                        <div className="mt-3">
                            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">
                                Paramètres de Délégation Globale
                            </h3>
                            <p className="text-xs sm:text-sm text-gray-600 mb-4 break-words">
                                Définissez les délégués par défaut pour tous les nouveaux lots de paiement.
                            </p>
                            
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                                        Premier délégué (après 4h)
                                    </label>
                                    <select
                                        value={globalDelegate1Id}
                                        onChange={(e) => setGlobalDelegate1Id(e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                    >
                                        <option value="">Sélectionner un utilisateur</option>
                                        {delegationUsers.map(user => (
                                            <option key={user.id} value={user.id}>
                                                {user.name} ({user.role})
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                                        Deuxième délégué (après 8h)
                                    </label>
                                    <select
                                        value={globalDelegate2Id}
                                        onChange={(e) => setGlobalDelegate2Id(e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                    >
                                        <option value="">Sélectionner un utilisateur</option>
                                        {delegationUsers.map(user => (
                                            <option key={user.id} value={user.id}>
                                                {user.name} ({user.role})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="flex flex-col sm:flex-row justify-end gap-3 sm:space-x-3 mt-6">
                                <button
                                    onClick={() => setShowGlobalDelegationModal(false)}
                                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                                    disabled={processingGlobalDelegation}
                                >
                                    Annuler
                                </button>
                                <button
                                    onClick={handleGlobalDelegation}
                                    disabled={processingGlobalDelegation || !globalDelegate1Id || !globalDelegate2Id}
                                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                >
                                    {processingGlobalDelegation && (
                                        <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                                    )}
                                    <span className="whitespace-nowrap">
                                        {processingGlobalDelegation ? 'Définition...' : 'Définir la Délégation Globale'}
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={showDeleteConfirmationModal}
                onClose={() => setShowDeleteConfirmationModal(false)}
                onConfirm={handleConfirmDeleteDelegation}
                title="Supprimer la Délégation Globale"
                message="Êtes-vous sûr de vouloir supprimer les paramètres de délégation globale ? Cette action ne peut pas être annulée."
                confirmText="Supprimer"
                cancelText="Annuler"
                loading={loading}
            />

            {/* Email Verification Modal (supervisors + F2F interviewers) */}
            {showEmailVerifyModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h3 className="text-2xl font-bold text-gray-900">
                                    <FontAwesomeIcon icon={faEnvelope} className="mr-2 text-red-600" />
                                    Vérifier l'adresse email
                                </h3>
                                <button
                                    onClick={() => setShowEmailVerifyModal(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <FontAwesomeIcon icon={faTimes} className="text-xl" />
                                </button>
                            </div>
                        </div>

                        <div className="p-6">
                            {emailVerifyError && (
                                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-red-700 text-sm">{emailVerifyError}</p>
                                </div>
                            )}
                            {emailVerifyInfo && (
                                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                                    <p className="text-green-700 text-sm">{emailVerifyInfo}</p>
                                </div>
                            )}

                            {emailVerifyStep === 'request' ? (
                                <div className="space-y-4">
                                    <p className="text-sm text-gray-600">
                                        Confirmez votre adresse email. Vous pouvez conserver la même
                                        adresse ou en saisir une nouvelle — un code de vérification à
                                        6 chiffres y sera envoyé.
                                    </p>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Adresse email
                                        </label>
                                        <div className="relative">
                                            <FontAwesomeIcon
                                                icon={faEnvelope}
                                                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                            />
                                            <input
                                                type="email"
                                                value={emailVerifyForm.email}
                                                onChange={(e) => setEmailVerifyForm({ ...emailVerifyForm, email: e.target.value })}
                                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                                                placeholder="exemple@email.com"
                                            />
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleSendEmailCode}
                                        disabled={emailVerifyLoading}
                                        className="w-full flex items-center justify-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold"
                                    >
                                        {emailVerifyLoading ? (
                                            <>
                                                <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                                                Envoi...
                                            </>
                                        ) : (
                                            'Envoyer le code'
                                        )}
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <p className="text-sm text-gray-600">
                                        Saisissez le code à 6 chiffres envoyé à
                                        {' '}<strong className="break-all">{emailVerifyForm.email}</strong>.
                                    </p>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Code de vérification
                                        </label>
                                        <div className="relative">
                                            <FontAwesomeIcon
                                                icon={faKey}
                                                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                            />
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                value={emailVerifyForm.code}
                                                onChange={(e) => setEmailVerifyForm({ ...emailVerifyForm, code: e.target.value.replace(/[^0-9]/g, '').slice(0, 6) })}
                                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none tracking-[0.5em] text-center text-lg"
                                                placeholder="______"
                                                maxLength={6}
                                            />
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleVerifyEmailCode}
                                        disabled={emailVerifyLoading}
                                        className="w-full flex items-center justify-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold"
                                    >
                                        {emailVerifyLoading ? (
                                            <>
                                                <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                                                Vérification...
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faCheck} className="mr-2" />
                                                Vérifier
                                            </>
                                        )}
                                    </button>
                                    <button
                                        onClick={() => { setEmailVerifyStep('request'); setEmailVerifyError(''); setEmailVerifyInfo(''); }}
                                        disabled={emailVerifyLoading}
                                        className="w-full px-6 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                                    >
                                        Modifier l'adresse / renvoyer un code
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Success Modal */}
            {showSuccessModal && (
                <SuccessModal
                    isOpen={showSuccessModal}
                    onClose={() => setShowSuccessModal(false)}
                    message={successMessage}
                />
            )}
        </div>
    );
};

export default UserProfile;
